import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, LogOut, CheckCircle2, XCircle, Navigation, Clock, AlertTriangle, Phone, CalendarClock, StickyNote, MessageCircle, UserX, ShieldAlert } from 'lucide-react';
import { checkZone, formatDistanceM, type JobCompletionChecklist } from '@conversia/shared-types';
import { JobTimeline } from '@/components/tracking/JobTimeline';
import * as meApi from '@/api/me.api';
import * as installationApi from '@/api/installation.api';
import type { JobWithRelations } from '@/api/jobs.api';
import { ApiError } from '@/api/client';
import { getBrowserLocation } from '@/lib/geolocation';
import { useAuth } from '@/hooks/useAuth';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { jobStatusBadge } from '@/lib/badges';
import { faltaParaCerrarEnSitio } from '@/lib/cierre-validacion';
import { buildMapsHref } from '@/lib/maps-href';
import { groupJobs } from '@/lib/job-groups';
import { ordenarParaTecnico, tipoDeAtencion } from '@/lib/garantia-prioridad';
import { cn } from '@/lib/cn';
import { useToast } from '@/hooks/useToast';
import { Cargando } from '@/components/common/Cargando';
import { CambiarClaveModal } from '@/components/common/CambiarClaveModal';

/** Ítems del checklist de cierre "Terminé", en orden de presentación. */
const CHECKLIST_ITEMS: { key: keyof JobCompletionChecklist; label: string }[] = [
  { key: 'articuloVerificado', label: 'Artículo verificado' },
  { key: 'sinProblemaPuerta', label: 'Sin problema con la puerta' },
  { key: 'areaLimpia', label: 'Área limpia' },
  { key: 'probada', label: 'Probada' },
];

const EMPTY_CHECKLIST: JobCompletionChecklist = {
  articuloVerificado: false,
  sinProblemaPuerta: false,
  areaLimpia: false,
  probada: false,
};

/** Cada cuánto se reporta la ubicación con la vista abierta (ms). */
const LOCATION_REPORT_INTERVAL_MS = 5 * 60_000;

/**
 * Reporte PASIVO de ubicación: al entrar a la vista y luego cada 5 minutos
 * mientras esté abierta (y visible). El permiso lo pide el navegador UNA vez;
 * el banner de la vista avisa que se está compartiendo. Best-effort: si el
 * técnico niega el permiso o falla la red, no molesta ni rompe nada.
 * Limitación asumida: con el navegador cerrado NO hay reporte.
 */
function useReporteUbicacion(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    let cancelado = false;
    const reportar = async () => {
      // Pestaña en segundo plano: no tiene sentido gastar GPS/batería.
      if (document.visibilityState === 'hidden') return;
      const loc = await getBrowserLocation();
      if (!cancelado && loc) {
        void meApi.reportLocation(loc.lat, loc.lng).catch(() => undefined);
      }
    };
    void reportar();
    const timer = window.setInterval(() => void reportar(), LOCATION_REPORT_INTERVAL_MS);
    return () => {
      cancelado = true;
      window.clearInterval(timer);
    };
  }, [enabled]);
}

/** Banda de color por estado (borde izquierdo de la tarjeta): lectura de un vistazo. */
const ESTADO_BORDER: Record<string, string> = {
  ASIGNADO: 'border-l-4 border-l-blue-500',
  EN_SITIO: 'border-l-4 border-l-amber-500',
  COMPLETADO: 'border-l-4 border-l-emerald-500',
  NO_COMPLETADO: 'border-l-4 border-l-red-500',
  CANCELADO: 'border-l-4 border-l-slate-400',
};

/** `tel:` con el teléfono normalizado a dígitos (+1809… abre el marcador). */
function telHref(phone: string): string {
  const digits = phone.replace(/[^\d]/g, '');
  return `tel:+${digits}`;
}

/** wa.me con solo dígitos (sin `+`): abre el chat en el WhatsApp del técnico. */
function waHref(phone: string): string {
  const digits = phone.replace(/[^\d]/g, '');
  return `https://wa.me/${digits}`;
}

/** Hora de la cita en grande ("3:30 p. m.") + día corto si no es hoy. */
function citaPartes(iso: string | null | undefined): { hora: string; dia: string | null } | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const hora = d.toLocaleTimeString('es-DO', { hour: 'numeric', minute: '2-digit' });
  const esHoy = d.toDateString() === new Date().toDateString();
  const dia = esHoy ? null : d.toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });
  return { hora, dia };
}

/**
 * Vista del técnico (capa de operaciones, PASO 3). UI mínima y propia (sin el CRM
 * ni el sidebar admin): SOLO sus trabajos asignados, con el ponche por estado
 * (Llegué / Terminé / No terminé). El backend acota todo a su empleado.
 */
export default function TecnicoJobs() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [claveOpen, setClaveOpen] = useState(false);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['me', 'jobs'],
    queryFn: () => meApi.listMyJobs(),
  });

  // Ficha propia (foto de perfil). Si la cuenta no es técnico, el 403 lo maneja
  // la query de trabajos; esta es best-effort para el avatar del header.
  const { data: yo } = useQuery({
    queryKey: ['me', 'employee'],
    queryFn: meApi.getMyEmployee,
    retry: false,
  });
  const subirFoto = useMutation({
    mutationFn: (file: File) => meApi.uploadMyPhoto(file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'employee'] });
      toast.success('Foto actualizada');
    },
    onError: () => toast.error('No se pudo subir la foto'),
  });

  // 403 = la cuenta no tiene ficha de técnico activa.
  const forbidden = isError && error instanceof ApiError && error.status === 403;
  // Reporte pasivo de ubicación (solo con ficha de técnico resuelta).
  useReporteUbicacion(Boolean(yo));
  const jobs = data?.items ?? [];
  // Mismo criterio que /jobs del panel: Pendientes / En curso / Terminados,
  // y dentro de cada grupo por hora de cita (la agenda real del día).
  // Dentro de cada grupo, lo URGENTE primero (garantía con el cliente afuera y
  // después las vencidas) y recién entonces por hora de cita. Antes el técnico
  // veía veinte tarjetas iguales y salía a la primera de la lista.
  const grupos = groupJobs(jobs).map((g) => ({ ...g, jobs: ordenarParaTecnico(g.jobs) }));
  const sinActivos = jobs.every((j) => j.estado === 'COMPLETADO' || j.estado === 'NO_COMPLETADO');

  return (
    <div className="min-h-full bg-surface-muted dark:bg-slate-950">
      <header className="flex h-16 items-center justify-between border-b border-surface-border bg-white px-4 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-2">
          {/* Avatar propio: click = cambiar la foto de perfil. */}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            title="Cambiar mi foto"
            aria-label="Cambiar mi foto"
            className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <Avatar name={yo?.nombre ?? user?.name} src={yo?.fotoUrl} size="md" />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) subirFoto.mutate(f);
              e.target.value = '';
            }}
          />
          <div className="leading-tight">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Mis trabajos</p>
            <p className="text-xs text-slate-400">
              {user?.name ?? user?.email}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="ml-1.5 text-brand-600 hover:underline"
              >
                {subirFoto.isPending ? 'Subiendo…' : yo?.fotoUrl ? 'Cambiar foto' : 'Subir foto'}
              </button>
              <button
                type="button"
                onClick={() => setClaveOpen(true)}
                className="ml-1.5 text-brand-600 hover:underline"
              >
                Cambiar contraseña
              </button>
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void logout()}>
          <LogOut className="h-4 w-4" /> Salir
        </Button>
      </header>
      <CambiarClaveModal open={claveOpen} onClose={() => setClaveOpen(false)} />

      <main className="mx-auto max-w-xl space-y-6 p-4">
        {isLoading ? (
          <Cargando tamano="pantalla" />
        ) : forbidden ? (
          <EmptyState
            icon={XCircle}
            title="Sin perfil de técnico"
            description="Su cuenta existe pero no está vinculada a un técnico. Pida al administrador que la vincule en: Empleados → editar su ficha → «Cuenta de acceso»."
            className="mt-10"
          />
        ) : (
          <>
            {/* Aviso HONESTO de ubicación: el técnico debe saber qué comparte. */}
            <p className="flex items-start gap-1.5 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Mientras esta pantalla esté abierta, su ubicación se comparte con la central para
              coordinar los trabajos. Al cerrar el navegador deja de compartirse.
            </p>
            {sinActivos && (
              <EmptyState icon={CheckCircle2} title="Nada pendiente" description="No tiene trabajos activos ahora mismo." />
            )}
            {grupos.map((grupo) => (
              <section key={grupo.key} className="space-y-4">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {grupo.label} ({grupo.jobs.length})
                </h2>
                {grupo.jobs.map((job) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </section>
            ))}
          </>
        )}
      </main>
    </div>
  );
}

function JobCard({ job }: { job: JobWithRelations }) {
  const badge = jobStatusBadge(job.estado);
  const cliente = job.contact?.name ?? job.contact?.pushName ?? job.contact?.phone ?? 'Cliente';
  const [closeModal, setCloseModal] = useState<null | 'completed' | 'not_completed'>(null);

  // Vencido: en sitio y pasado el SLA (2h). Se resalta con un nudge para empujar
  // el cierre — el aviso al dueño ya lo dispara el worker en el backend.
  const overdue =
    job.estado === 'EN_SITIO' && !!job.slaDeadline && new Date(job.slaDeadline).getTime() < Date.now();

  // URGENTE: el cliente está en la calle. Es la regla textual del cliente —
  // "si el cliente está afuera siempre es la prioridad" — y hasta ahora solo se
  // veía en el panel. En un trabajo ya cerrado no se muestra: no hay nada que
  // apurar y el rojo dejaría de significar urgencia.
  const abierto = job.estado === 'ASIGNADO' || job.estado === 'EN_SITIO';
  const urgente = abierto && job.clienteSinAcceso === true;
  // S-04 · Un caso por teléfono no tiene a dónde ir: se dice en la tarjeta para
  // que el técnico no salga a manejar por algo que se resuelve llamando.
  const atencion = tipoDeAtencion(job);

  // Chequeo de zona: ubicación de llegada vs objetivo del trabajo (null si falta algo).
  const zone =
    job.arrivedLat != null && job.arrivedLng != null
      ? checkZone(
          { lat: job.arrivedLat, lng: job.arrivedLng },
          job.targetLat != null && job.targetLng != null
            ? { lat: job.targetLat, lng: job.targetLng }
            : null,
          job.geofenceRadiusM ?? null,
        )
      : null;

  const cita = citaPartes(job.scheduledAt);
  const mapsHref = buildMapsHref(job);

  // Tocar el caso (llamar, escribir o pedir cómo llegar) es el momento en que
  // la garantía deja de estar esperando y pasa a estar en manos de alguien. Se
  // avisa al servidor para que empiece a recordarle que complete la ficha.
  //
  // Best-effort y sin bloquear: el `tel:`/`wa.me`/Maps abre igual. Un error acá
  // no es problema del técnico y no tiene por qué interrumpirlo.
  const abrirCaso = () => {
    if (!job.esGarantia || job.seguimientoAbiertoAt) return;
    void meApi.abrirSeguimiento(job.id).catch(() => undefined);
  };

  return (
    <Card
      className={cn(
        'shadow-sm',
        ESTADO_BORDER[job.estado],
        overdue && 'border-danger ring-1 ring-danger/40',
        // El cliente en la calle manda sobre todo lo demás, incluso sobre el
        // color del estado: tiene que saltar a la vista antes que nada.
        urgente && 'border-l-4 border-l-red-600 border-danger ring-2 ring-danger/50',
      )}
    >
      <CardBody className="space-y-3">
        {/* Jerarquía móvil: hora y cliente ARRIBA (lo que el técnico mira primero). */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {cita ? (
              <p className="flex items-baseline gap-1.5 text-slate-900 dark:text-slate-100">
                <CalendarClock className="h-4 w-4 shrink-0 self-center text-slate-400" />
                <span className="text-xl font-bold leading-tight">{cita.hora}</span>
                {cita.dia && <span className="text-xs text-slate-500 dark:text-slate-400">{cita.dia}</span>}
              </p>
            ) : (
              <p className="text-xs text-slate-400">Sin hora de cita</p>
            )}
            <p className="mt-0.5 truncate text-base font-semibold text-slate-900 dark:text-slate-100">{cliente}</p>
            <p className="text-sm text-slate-600 dark:text-slate-300">{job.descripcion}</p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            {urgente && (
              <Badge variant="danger" className="font-bold uppercase tracking-wide">
                <AlertTriangle className="h-3.5 w-3.5" /> Urgente
              </Badge>
            )}
            <Badge variant={badge.variant}>{badge.label}</Badge>
            {job.esGarantia && (
              <Badge variant="warning">
                <ShieldAlert className="h-3.5 w-3.5" /> Garantía
              </Badge>
            )}
            <Badge variant={atencion.vaAlSitio ? 'default' : 'info'}>{atencion.etiqueta}</Badge>
          </div>
        </div>
        {urgente && (
          <p className="flex items-center gap-1.5 rounded-md bg-red-50 px-2 py-1.5 text-sm font-semibold text-danger dark:bg-red-900/20">
            <AlertTriangle className="h-4 w-4 shrink-0" /> Cliente fuera de su casa
          </p>
        )}

        {/* Progreso tipo Uber: en camino → llegó → realizado. */}
        <JobTimeline estado={job.estado} />

        {/* En una VISITA lo primero es la dirección y "Cómo llegar"; en un caso
            POR TELÉFONO no hay a dónde ir, así que lo primero son Llamar y
            WhatsApp. Sin esto, el botón azul más grande de la tarjeta invitaba
            a manejar hasta la casa de alguien por algo que se resolvía
            llamando. Las notas quedan en el medio en los dos casos: el técnico
            lee este bloque entero antes de moverse. */}
        {atencion.vaAlSitio ? (
          <>
            <BloqueUbicacion job={job} mapsHref={mapsHref} principal onAbrirCaso={abrirCaso} />
            <BloqueNotas job={job} />
            <BloqueContacto job={job} onAbrirCaso={abrirCaso} />
          </>
        ) : (
          <>
            <BloqueContacto job={job} onAbrirCaso={abrirCaso} />
            <BloqueNotas job={job} />
            <BloqueUbicacion
              job={job}
              mapsHref={mapsHref}
              principal={false}
              onAbrirCaso={abrirCaso}
            />
          </>
        )}
        {job.estado === 'EN_SITIO' && job.arrivedLat == null && (
          <p className="text-xs text-slate-400">
            Sin ubicación registrada (el GPS estaba apagado o sin permiso). El ponche vale igual.
          </p>
        )}
        {job.arrivedLat != null && (
          <p className="text-xs">
            {zone ? (
              zone.enZona ? (
                <span className="text-emerald-600 dark:text-emerald-400">
                  Dentro de zona ({formatDistanceM(zone.distanceM)} del objetivo)
                </span>
              ) : (
                <span className="text-danger">
                  Fuera de zona ({formatDistanceM(zone.distanceM)} del objetivo)
                </span>
              )
            ) : (
              <span className="text-slate-400">
                Ubicación capturada ({job.arrivedLat.toFixed(5)}, {job.arrivedLng?.toFixed(5)})
              </span>
            )}
          </p>
        )}
        {overdue ? (
          <p className="flex items-center gap-1.5 rounded-md bg-red-50 px-2 py-1.5 text-xs font-medium text-danger dark:bg-red-900/20">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Pasó la hora de seguimiento sin cerrar. ¿Ya terminó? Marque Terminé o No terminé.
          </p>
        ) : (
          job.estado === 'EN_SITIO' && job.slaDeadline && (
            <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
              <Clock className="h-3.5 w-3.5" /> En sitio · seguimiento a las {new Date(job.slaDeadline).toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )
        )}
        {job.resultado && <p className="text-xs text-slate-500">Resultado: {job.resultado}</p>}
        {job.motivo && <p className="text-xs text-slate-500">Motivo: {job.motivo}</p>}

        <PunchActions job={job} onClose={(kind) => setCloseModal(kind)} />

        {/* "Cliente no llegó" (Fase 2): reporta a la central — NO cancela (la
            decisión es del panel). Un solo aviso por trabajo. */}
        {(job.estado === 'ASIGNADO' || job.estado === 'EN_SITIO') &&
          (job.noShowReportedAt ? (
            <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
              <UserX className="h-3.5 w-3.5 shrink-0" /> Ya se avisó a la central que el cliente no
              llegó. Espere instrucciones.
            </p>
          ) : (
            <NoShowButton job={job} />
          ))}
      </CardBody>

      <PunchCloseModal job={job} kind={closeModal} onDone={() => setCloseModal(null)} />
    </Card>
  );
}

/**
 * Botón "Cliente no llegó" con confirmación (Fase 2). Reporta el no-show a la
 * central; el trabajo sigue igual hasta que el panel decida (cancelar o
 * reagendar). El backend garantiza un solo aviso por trabajo.
 */
function NoShowButton({ job }: { job: JobWithRelations }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmar, setConfirmar] = useState(false);

  const report = useMutation({
    mutationFn: () => meApi.reportNoShow(job.id),
    onSuccess: ({ alreadyReported }) => {
      void qc.invalidateQueries({ queryKey: ['me', 'jobs'] });
      toast.success(
        alreadyReported ? 'Ya se había avisado a la central' : 'Avisado a la central: cliente no llegó',
      );
      setConfirmar(false);
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : 'No se pudo enviar el aviso');
      setConfirmar(false);
    },
  });

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="w-full text-amber-700 dark:text-amber-400"
        onClick={() => setConfirmar(true)}
      >
        <UserX className="h-4 w-4" /> Cliente no llegó
      </Button>
      <Modal
        open={confirmar}
        onClose={() => setConfirmar(false)}
        title="¿El cliente no se presentó?"
        size="sm"
        description="Se avisará a la central. El trabajo NO se cancela: espere instrucciones (cancelar o reagendar lo decide la oficina)."
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setConfirmar(false)}>
              Volver
            </Button>
            <Button size="sm" onClick={() => report.mutate()} loading={report.isPending}>
              Sí, avisar
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Cliente: {job.contact?.name ?? job.contact?.pushName ?? job.contact?.phone ?? '—'}
        </p>
      </Modal>
    </>
  );
}

/**
 * Dirección + "Cómo llegar". `principal` decide si el botón manda (visita) o va
 * apagado (caso por teléfono: no hay a dónde ir, pero el dato sigue estando por
 * si el caso termina escalando a visita).
 */
function BloqueUbicacion({
  job,
  mapsHref,
  principal,
  onAbrirCaso,
}: {
  job: JobWithRelations;
  mapsHref: string | null;
  principal: boolean;
  onAbrirCaso: () => void;
}) {
  if (!job.zona && !job.direccion && !mapsHref) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      {(job.zona || job.direccion) && (
        <p className="flex min-w-0 items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
          <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
          <span className="min-w-0">{[job.zona, job.direccion].filter(Boolean).join(' · ')}</span>
        </p>
      )}
      {/* Cómo llegar (Bloque 1 ubicación): abre Google Maps con el link del
          cliente → coordenadas objetivo → búsqueda por dirección. */}
      {mapsHref && (
        <a
          href={mapsHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onAbrirCaso}
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold',
            principal
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300',
          )}
        >
          <Navigation className="h-4 w-4" /> Cómo llegar
        </a>
      )}
    </div>
  );
}

/**
 * S-16 · Indicaciones que dejó el panel. Van pegadas al bloque de dirección y
 * contacto a propósito: el técnico lee ese bloque entero antes de salir, y una
 * nota separada del resto se saltea.
 */
function BloqueNotas({ job }: { job: JobWithRelations }) {
  if (!job.notas) return null;
  return (
    <p className="flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
      <StickyNote className="mt-0.5 h-4 w-4 shrink-0" /> {job.notas}
    </p>
  );
}

/** Teléfono del cliente con Llamar y WhatsApp. */
function BloqueContacto({
  job,
  onAbrirCaso,
}: {
  job: JobWithRelations;
  onAbrirCaso: () => void;
}) {
  if (!job.contact?.phone) return null;
  const phone = job.contact.phone;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-surface-muted px-3 py-2 dark:bg-slate-800/60">
      <span className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
        <Phone className="h-4 w-4 shrink-0 text-slate-400" /> {phone}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {/* tel: abre el marcador; wa.me abre el chat en el WhatsApp del técnico. */}
        <a
          href={telHref(phone)}
          onClick={onAbrirCaso}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          <Phone className="h-4 w-4" /> Llamar
        </a>
        <a
          href={waHref(phone)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onAbrirCaso}
          className="inline-flex items-center gap-1.5 rounded-lg bg-green-500 px-4 py-2 text-sm font-semibold text-white hover:bg-green-600"
        >
          <MessageCircle className="h-4 w-4" /> WhatsApp
        </a>
      </span>
    </div>
  );
}

/** Botones de ponche según el estado del trabajo. */
function PunchActions({ job, onClose }: { job: JobWithRelations; onClose: (kind: 'completed' | 'not_completed') => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  const arrived = useMutation({
    mutationFn: async () => {
      // Captura la ubicación (no bloquea: si la niega/falla, va null).
      const loc = await getBrowserLocation();
      await meApi.punch(job.id, { action: 'arrived', lat: loc?.lat ?? null, lng: loc?.lng ?? null });
      return { conGps: Boolean(loc) };
    },
    onSuccess: ({ conGps }) => {
      void qc.invalidateQueries({ queryKey: ['me', 'jobs'] });
      // El técnico DEBE saber si el GPS no salió: así activa el permiso la próxima.
      if (conGps) toast.success('Marcado: Llegué');
      else toast.success('Marcado: Llegué — sin ubicación (active el GPS del teléfono)');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'No se pudo registrar'),
  });

  // Botones GRANDES a propósito: se usan en la calle, con sol y de apuro.
  if (job.estado === 'ASIGNADO') {
    return (
      <Button size="lg" className="w-full text-base" onClick={() => arrived.mutate()} loading={arrived.isPending}>
        <Navigation className="h-5 w-5" /> Llegué
      </Button>
    );
  }
  if (job.estado === 'EN_SITIO') {
    return (
      <div className="grid grid-cols-2 gap-2">
        <Button size="lg" className="text-base" onClick={() => onClose('completed')}>
          <CheckCircle2 className="h-5 w-5" /> Terminé
        </Button>
        <Button size="lg" className="text-base" variant="outline" onClick={() => onClose('not_completed')}>
          <XCircle className="h-5 w-5" /> No terminé
        </Button>
      </div>
    );
  }
  return null; // estado terminal: sin acciones
}

/**
 * Modal de cierre del ponche:
 *  - Terminé → S-01 (tanda EMT-CNV-2026-01): MODELO instalado (del catálogo del
 *    panel), NÚMERO DE SERIE y resultado del trabajo, los tres obligatorios;
 *    más el checklist de verificación. Al confirmar, el trabajo NO queda
 *    cerrado: pasa a "Por validar" y supervisión completa monto, forma de pago
 *    y vendedor desde el panel.
 *  - No terminé → motivo obligatorio (nota libre).
 */
function PunchCloseModal({
  job,
  kind,
  onDone,
}: {
  job: JobWithRelations;
  kind: null | 'completed' | 'not_completed';
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [motivo, setMotivo] = useState('');
  const [checklist, setChecklist] = useState<JobCompletionChecklist>(EMPTY_CHECKLIST);
  const [registro, setRegistro] = useState('');
  const [modelo, setModelo] = useState('');
  const [numeroSerie, setNumeroSerie] = useState('');
  const [resultado, setResultado] = useState('');
  // S-02 · Tipificación de la garantía que PROPONE el técnico.
  const [nivel, setNivel] = useState('');
  const [causa, setCausa] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isNotCompleted = kind === 'not_completed';

  // Catálogo de modelos del panel: el técnico ELIGE, no escribe. Así el modelo
  // queda comparable con el período de cobertura de la garantía.
  const crmView = useQuery({ queryKey: ['installation', 'crm'], queryFn: installationApi.getCrmView });
  const modelos = crmView.data?.trabajos?.modelos ?? [];
  const niveles = (crmView.data?.trabajos?.nivelesResolucion ?? []).filter((n) => n.activo);
  const causas = (crmView.data?.trabajos?.causasRaiz ?? []).filter((c) => c.activo);
  // S-07 · El motivo de "No terminé" sale del catálogo del panel, no se escribe.
  const motivos = (crmView.data?.trabajos?.motivosNoCompletado ?? []).filter((m) => m.activo);
  // Solo las garantías piden tipificación.
  const esGarantia = job.esGarantia === true;

  const submit = useMutation({
    mutationFn: async () => {
      // Ubicación del cierre (best-effort; refresca la última posición del técnico).
      const loc = await getBrowserLocation();
      if (isNotCompleted) {
        await meApi.punch(job.id, { action: 'not_completed', motivo: motivo.trim(), lat: loc?.lat ?? null, lng: loc?.lng ?? null });
      } else {
        await meApi.punch(job.id, {
          action: 'completed',
          modelo: modelo.trim(),
          numeroSerie: numeroSerie.trim(),
          resultado: resultado.trim(),
          nivelResolucionPropuesto: esGarantia ? nivel : null,
          causaRaizPropuesta: esGarantia ? causa : null,
          checklist,
          registroArticulo: registro.trim() || null,
          lat: loc?.lat ?? null,
          lng: loc?.lng ?? null,
        });
      }
      return { conGps: Boolean(loc) };
    },
    onSuccess: ({ conGps }) => {
      void qc.invalidateQueries({ queryKey: ['me', 'jobs'] });
      // S-01: "Terminé" ya no cierra el trabajo. Se le dice al técnico qué pasó
      // de verdad, para que no crea que quedó algo pendiente de su lado.
      const base = isNotCompleted
        ? 'Marcado: No terminé'
        : 'Trabajo enviado a la oficina para su cierre';
      toast.success(conGps ? base : `${base} — sin ubicación (active el GPS del teléfono)`);
      close();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'No se pudo registrar'),
  });

  function close() {
    setMotivo('');
    setChecklist(EMPTY_CHECKLIST);
    setRegistro('');
    setModelo('');
    setNumeroSerie('');
    setResultado('');
    setNivel('');
    setCausa('');
    setError(null);
    onDone();
  }

  function handle() {
    if (isNotCompleted) {
      if (!motivo.trim()) {
        setError('El motivo es obligatorio');
        return;
      }
    } else {
      // Mensaje en llano y de a UNO: el técnico está en la puerta de un cliente
      // y una lista de tres faltas junta no le dice cuál arreglar primero.
      const falta = faltaParaCerrarEnSitio({ modelo, numeroSerie, resultado });
      if (falta) {
        setError(falta);
        return;
      }
    }
    if (!isNotCompleted && esGarantia && (!nivel || !causa)) {
      setError('En una garantía hay que indicar cómo se resolvió y por qué falló.');
      return;
    }
    setError(null);
    submit.mutate();
  }

  return (
    <Modal
      open={kind !== null}
      onClose={close}
      title={isNotCompleted ? 'No terminé el trabajo' : 'Terminé el trabajo'}
      description={
        isNotCompleted
          ? 'Indique el motivo por el que no se completó.'
          : 'Registre el modelo y el número de serie instalados. La oficina completa el cobro y cierra el trabajo.'
      }
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close}>
            Cancelar
          </Button>
          <Button onClick={handle} loading={submit.isPending}>
            Confirmar
          </Button>
        </>
      }
    >
      {isNotCompleted ? (
        <div className="space-y-2">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              ¿Por qué no se pudo?
            </span>
            <select
              className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            >
              <option value="">Elija el motivo…</option>
              {motivos.map((m) => (
                <option key={m.id} value={m.nombre}>
                  {m.nombre}
                </option>
              ))}
            </select>
          </label>
          {motivos.length === 0 && !crmView.isLoading && (
            <p className="text-xs text-danger">
              No hay motivos cargados. Avise a la oficina: no se puede marcar «No terminé» sin uno.
            </p>
          )}
          {error && <p className="text-sm text-danger">{error}</p>}
        </div>
      ) : (
        <div className="space-y-4">
          {/* S-01: los tres datos que el técnico llena EN SITIO. */}
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Modelo instalado
            </span>
            <select
              className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              value={modelo}
              onChange={(e) => setModelo(e.target.value)}
            >
              <option value="">Elija el modelo…</option>
              {modelos.map((m) => (
                <option key={m.id} value={m.nombre}>
                  {m.nombre}
                </option>
              ))}
            </select>
            {modelos.length === 0 && !crmView.isLoading && (
              <span className="mt-1 block text-xs text-danger">
                No hay modelos cargados. Avise a la oficina: no se puede cerrar sin modelo.
              </span>
            )}
          </label>
          <Input
            label="Número de serie"
            placeholder="El que trae el equipo instalado"
            value={numeroSerie}
            onChange={(e) => setNumeroSerie(e.target.value)}
          />
          {/* La ficha es OBLIGATORIA en todo cierre (pedido del cliente): antes
              se escribía "ok" y la oficina no se enteraba de qué pasó en esa
              casa. Se le dice acá mismo, no recién al apretar el botón. */}
          <Textarea
            label="Resultado del trabajo (obligatorio)"
            placeholder="Qué se hizo y cómo quedó — por ejemplo: se cambió el módulo de huella y quedó probada"
            rows={3}
            value={resultado}
            onChange={(e) => setResultado(e.target.value)}
          />
          <p className="-mt-2 text-xs text-slate-500">
            Cuente en una línea qué hizo. Con «ok» no alcanza: esto es lo que lee la oficina.
          </p>
          {/* S-02: solo en garantías. El técnico PROPONE; la oficina confirma. */}
          {esGarantia && (
            <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50/60 p-3">
              <p className="text-xs font-medium text-amber-900">
                Esta visita es una garantía. Indique cómo se resolvió y por qué falló — la oficina
                lo revisa antes de cerrar.
              </p>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  ¿Cómo se resolvió?
                </span>
                <select
                  className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  value={nivel}
                  onChange={(e) => setNivel(e.target.value)}
                >
                  <option value="">Elija una opción…</option>
                  {niveles.map((n) => (
                    <option key={n.id} value={n.codigo}>
                      {n.nombre}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  ¿Por qué falló?
                </span>
                <select
                  className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  value={causa}
                  onChange={(e) => setCausa(e.target.value)}
                >
                  <option value="">Elija una opción…</option>
                  {causas.map((c) => (
                    <option key={c.id} value={c.nombre}>
                      {c.nombre}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-700 dark:text-slate-300">Verificación</legend>
            {CHECKLIST_ITEMS.map((item) => (
              <label key={item.key} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  checked={checklist[item.key]}
                  onChange={(e) => setChecklist((c) => ({ ...c, [item.key]: e.target.checked }))}
                />
                {item.label}
              </label>
            ))}
          </fieldset>
          <Input
            label="Datos de registro de el artículo"
            placeholder="Código / serie que indica el cliente"
            value={registro}
            onChange={(e) => setRegistro(e.target.value)}
          />
          {error && <p className="text-sm text-danger">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
